import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CelebrationIcon from '@mui/icons-material/Celebration';

export const AnnouncementsSection = () => {
  const teamRecognition = [
    {
      title: 'Employee of the Month',
      winner: 'Sarah Miller',
      department: 'Human Resources',
      achievement: 'Outstanding leadership in employee engagement initiatives',
      icon: <EmojiEventsIcon sx={{ fontSize: 50, color: '#ffd700' }} />,
    },
    {
      title: 'Innovation Award',
      winner: 'David Anderson',
      department: 'Data & Analytics',
      achievement: 'Developed AI-powered analytics dashboard',
      icon: <TrendingUpIcon sx={{ fontSize: 50, color: '#4caf50' }} />,
    },
    {
      title: 'Team Excellence',
      winner: 'Marketing Team',
      department: 'Marketing & Growth',
      achievement: 'Achieved 150% of quarterly growth targets',
      icon: <CelebrationIcon sx={{ fontSize: 50, color: '#ff9800' }} />,
    },
  ];

  const latestUpdates = [
    {
      title: 'Q4 All-Hands Meeting',
      date: 'Dec 15, 2025',
      description: 'Join us for the quarterly review and 2026 planning session',
      type: 'Event',
    },
    {
      title: 'New Office Opening',
      date: 'Dec 20, 2025',
      description: 'Grand opening of our new Singapore office',
      type: 'Announcement',
    },
    {
      title: 'Holiday Schedule',
      date: 'Dec 24-26, 2025',
      description: 'Office will be closed for Christmas holidays',
      type: 'Notice',
    },
    {
      title: 'Product Launch',
      date: 'Jan 5, 2026',
      description: 'Orunal360 v2.0 goes live with new features',
      type: 'Product',
    },
    {
      title: 'Team Building Event',
      date: 'Jan 12, 2026',
      description: 'Annual team retreat and celebration',
      type: 'Event',
    },
  ];

  return (
    <Box sx={{ py: 10, background: '#f5f5f5' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          sx={{ mb: 6, fontWeight: 700, color: '#1a1a1a' }}
        >
          Announcements & Recognition
        </Typography>

        <Grid container spacing={4}>
          {/* Team Recognition Carousel */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: 600, color: '#fff', textAlign: 'center' }}
              >
                Team Recognition
              </Typography>
              <Carousel
                indicators={true}
                navButtonsAlwaysVisible={false}
                autoPlay={true}
                interval={5000}
                animation="slide"
                sx={{
                  '& .MuiIconButton-root': {
                    color: '#fff',
                  },
                }}
              >
                {teamRecognition.map((recognition, index) => (
                  <Box
                    key={index}
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{recognition.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, fontWeight: 600, color: '#fff' }}
                    >
                      {recognition.title}
                    </Typography>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: '#fff',
                        color: '#764ba2',
                        fontSize: '2rem',
                        fontWeight: 700,
                      }}
                    >
                      {recognition.winner.charAt(0)}
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ mb: 1, fontWeight: 700, color: '#fff' }}
                    >
                      {recognition.winner}
                    </Typography>
                    <Chip
                      label={recognition.department}
                      sx={{
                        mb: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {recognition.achievement}
                    </Typography>
                  </Box>
                ))}
              </Carousel>
            </Paper>
          </Grid>

          {/* Latest Updates */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                background: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AnnouncementIcon sx={{ fontSize: 32, color: '#667eea', mr: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Latest Updates
                </Typography>
              </Box>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {latestUpdates.map((update, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: '#f9f9f9',
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: '#f0f0f0',
                        transform: 'translateX(5px)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {update.title}
                          </Typography>
                          <Chip
                            label={update.type}
                            size="small"
                            sx={{
                              bgcolor: '#667eea',
                              color: '#fff',
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                            {update.date}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {update.description}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AnnouncementsSection;